// RaceSettingsPanel.jsx
// ===================================================================
// Pannello di configurazione runtime per la direzione gara.
// Permette di modificare il jitter buffer delay e i parametri del
// dead reckoning senza riavviare il server. Le modifiche vengono
// applicate immediatamente anche durante la gara.
// ===================================================================
import React, { useEffect, useState, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

// Descrizioni umane dei parametri
const FIELDS = [
  {
    group: 'Jitter Buffer',
    description: 'Ritardo applicato ai pacchetti prima della trasmissione ai client. Compensa il jitter di rete e produce una visualizzazione fluida.',
    items: [
      { key: 'jitterDelayMs', label: 'Delay jitter buffer', unit: 'ms', step: 50, help: 'Ritardo fisso applicato a ogni pacchetto GPS in arrivo. Valore tipico: 2000 ms.' },
    ],
  },
  {
    group: 'Dead Reckoning — Soglie temporali',
    description: 'Definiscono il comportamento quando un device smette di inviare dati. I valori devono essere crescenti: LIVE → LOST → GONE.',
    items: [
      { key: 'drLiveMaxMs', label: 'Fine LIVE',    unit: 'ms', step: 100, help: 'Sotto questa soglia il pilota è in LIVE, nessuna simulazione. Tipico: 500 ms (copre il jitter di rete).' },
      { key: 'drLostMs',    label: 'Inizio fade',  unit: 'ms', step: 500, help: 'Da qui il marker comincia a sbiadire sulla mappa (fadeout).' },
      { key: 'drGoneMs',    label: 'Scomparsa',    unit: 'ms', step: 500, help: 'Oltre questa soglia il pilota viene rimosso dalla telemetria.' },
    ],
  },
  {
    group: 'Dead Reckoning — Modello di simulazione',
    description: 'Controlla come il pilota si muove sulla mappa durante la simulazione.',
    items: [
      { key: 'drDecayPerSec', label: 'Decay velocità/s', unit: '',    step: 0.02, help: 'Fattore moltiplicativo applicato alla velocità ogni secondo (0.90 = −10%/s, 1.00 = velocità costante).' },
      { key: 'drMinSpeedMs',  label: 'Velocità min',     unit: 'm/s', step: 0.5,  help: 'Sotto questa velocità il pilota è considerato fermo e non viene simulato (FROZEN).' },
    ],
  },
  {
    group: 'Alert direzione gara',
    description: 'Soglia di generazione degli alert per la direzione gara.',
    items: [
      { key: 'drWarningMs', label: 'Soglia warning', unit: 'ms', step: 100, help: 'Oltre questo tempo senza pacchetti, il pilota genera un alert giallo.' },
    ],
  },
];

function formatValue(key, v, bounds) {
  if (v == null) return '';
  const b = bounds?.[key];
  if (b?.int) return String(Math.round(v));
  // 2 decimali per i float
  return String(+Number(v).toFixed(2));
}

export default function RaceSettingsPanel({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Carica le settings dal backend quando si apre il pannello
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/race/settings`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data?.ok) {
          setSettings(data.settings);
          setDefaults(data.defaults);
          setBounds(data.bounds);
          setDirty(false);
        } else {
          setError(data?.error || 'Errore caricamento impostazioni');
        }
      })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const handleChange = useCallback((key, value) => {
    setSettings(prev => prev ? { ...prev, [key]: value } : prev);
    setDirty(true);
  }, []);

  const handleApply = useCallback(async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/race/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      const data = await res.json();
      if (data?.ok) {
        setSettings(data.settings);
        setDirty(false);
        setToast({ kind: 'ok', text: 'Impostazioni applicate' });
      } else {
        setError(data?.error || 'Errore salvataggio');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [settings]);

  const handleReset = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/race/settings/reset`, { method: 'POST' });
      const data = await res.json();
      if (data?.ok) {
        setSettings(data.settings);
        setDirty(false);
        setToast({ kind: 'ok', text: 'Ripristinati i valori di default' });
      } else {
        setError(data?.error || 'Errore reset');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  if (!open) return null;

  return (
    <div className="rs-settings-backdrop" onClick={onClose}>
      <div className="rs-settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="rs-settings-header">
          <div className="rs-settings-header-left">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <div>
              <div className="rs-settings-title">Impostazioni Gara</div>
              <div className="rs-settings-subtitle">Delay e Dead Reckoning — applicate in tempo reale</div>
            </div>
          </div>
          <button className="rs-settings-close" onClick={onClose} aria-label="Chiudi">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="rs-settings-body">
          {loading && !settings && (
            <div className="rs-settings-loading">Caricamento…</div>
          )}

          {error && (
            <div className="rs-settings-error">{error}</div>
          )}

          {settings && FIELDS.map((group) => (
            <div key={group.group} className="rs-settings-group">
              <div className="rs-settings-group-head">
                <div className="rs-settings-group-title">{group.group}</div>
                <div className="rs-settings-group-desc">{group.description}</div>
              </div>
              <div className="rs-settings-fields">
                {group.items.map((f) => {
                  const b = bounds?.[f.key] || {};
                  const def = defaults?.[f.key];
                  const val = settings[f.key];
                  const isDefault = def != null && Number(val) === Number(def);
                  return (
                    <div key={f.key} className="rs-settings-field">
                      <div className="rs-settings-field-head">
                        <label className="rs-settings-field-label">{f.label}</label>
                        {!isDefault && def != null && (
                          <button
                            className="rs-settings-field-reset"
                            onClick={() => handleChange(f.key, def)}
                            title={`Ripristina default (${formatValue(f.key, def, bounds)})`}
                          >
                            ↺ {formatValue(f.key, def, bounds)}
                          </button>
                        )}
                      </div>
                      <div className="rs-settings-field-input">
                        <input
                          type="number"
                          min={b.min}
                          max={b.max}
                          step={f.step}
                          value={formatValue(f.key, val, bounds)}
                          onChange={(e) => handleChange(f.key, e.target.value)}
                        />
                        <span className="rs-settings-field-unit">{f.unit}</span>
                      </div>
                      <div className="rs-settings-field-help">{f.help}</div>
                      <div className="rs-settings-field-range">
                        <span>min {b.min}</span>
                        <span>max {b.max}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="rs-settings-footer">
          <div className="rs-settings-footer-left">
            {toast && (
              <div className={`rs-settings-toast rs-settings-toast-${toast.kind}`}>
                {toast.text}
              </div>
            )}
          </div>
          <div className="rs-settings-footer-right">
            <button
              className="rs-settings-btn rs-settings-btn-ghost"
              onClick={handleReset}
              disabled={saving || loading}
            >
              Reset defaults
            </button>
            <button
              className="rs-settings-btn rs-settings-btn-primary"
              onClick={handleApply}
              disabled={!dirty || saving || loading}
            >
              {saving ? 'Applico…' : 'Applica'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
